/**
 * Collaboration Cursors — animated cursor trio for sign-in page.
 *
 * Three personas (Anna, Max, Lea) move along random Bézier curves.
 * Writes positions to cursorStore so DotGrid and BackgroundArts react.
 * Positions are updated continuously — including when paused.
 */
import { useEffect, useRef } from 'react';

import {
  type CursorPosition,
  deactivateCursor,
  moveCursorTo,
  registerCursor,
} from './cursor-store';

interface Persona {
  name: string;
  color: string;
}

const PERSONAS: Persona[] = [
  { name: 'Anna', color: '#FF6B35' },
  { name: 'Max', color: '#0891B2' },
  { name: 'Lea', color: '#7B61FF' },
];

const CURSOR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none"><path fill="currentColor" d="M3.03 5.302c-.278-1.606 1.41-2.832 2.851-2.07l13.518 7.14c1.542.815 1.336 3.086-.328 3.609l-5.526 1.736a1.96 1.96 0 0 0-1.01.734L9.17 21.174c-1.012 1.421-3.236.915-3.534-.804z"/></svg>`;

function bezAt(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number
): number {
  const u = 1 - t;
  return (
    u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
  );
}

function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

interface AnimatedCursor {
  el: HTMLDivElement;
  store: CursorPosition;
  persona: Persona;
}

export function CollaborationCursors() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;

    const cursors: AnimatedCursor[] = [];

    for (const persona of PERSONAS) {
      const el = document.createElement('div');
      el.innerHTML = `${CURSOR_SVG}<div style="background:${persona.color};margin-left:14px;margin-top:-2px;color:white;padding:0 3px;border:1px solid rgba(0,0,0,0.15);box-shadow:0 1px 4px rgba(0,0,0,0.12);border-radius:4px;font-size:11px;line-height:16px;white-space:nowrap">${persona.name}</div>`;
      el.style.cssText = `position:absolute;z-index:5;pointer-events:none;color:${persona.color};opacity:0;transition:opacity 0.6s ease;will-change:transform;`;
      container.appendChild(el);

      const store = registerCursor();
      cursors.push({ el, store, persona });
    }

    let cancelled = false;

    async function animateCursor(cursor: AnimatedCursor, index: number) {
      const cw = container!.clientWidth;
      const ch = container!.clientHeight;

      // Set initial position
      let cx = cw * (0.2 + Math.random() * 0.6);
      let cy = ch * (0.2 + Math.random() * 0.6);
      cursor.el.style.left = `${cx}px`;
      cursor.el.style.top = `${cy}px`;

      // Update store with viewport coords
      const rect = container!.getBoundingClientRect();
      moveCursorTo(cursor.store, rect.left + cx, rect.top + cy);

      // Staggered fade-in
      await sleep(500 + index * 800);
      cursor.el.style.opacity = '0.85';

      while (!cancelled) {
        const w = container!.clientWidth;
        const h = container!.clientHeight;

        const tx = w * (0.1 + Math.random() * 0.8);
        const ty = h * (0.1 + Math.random() * 0.8);

        const sx = cx;
        const sy = cy;
        const dx = tx - sx;
        const dy = ty - sy;
        const dist = Math.hypot(dx, dy);

        const perpX = dist > 0 ? -dy / dist : 0;
        const perpY = dist > 0 ? dx / dist : 0;
        const spread = dist * 0.3;
        const c1x = sx + dx * 0.25 + perpX * (Math.random() - 0.4) * spread;
        const c1y = sy + dy * 0.25 + perpY * (Math.random() - 0.4) * spread;
        const c2x = sx + dx * 0.75 + perpX * (Math.random() - 0.6) * spread;
        const c2y = sy + dy * 0.75 + perpY * (Math.random() - 0.6) * spread;

        const ms = Math.max(600, Math.min(2000, dist * 2));

        // Animate along Bézier
        await new Promise<void>(resolve => {
          const start = performance.now();
          function tick(now: number) {
            if (cancelled) {
              resolve();
              return;
            }
            const rawT = Math.min(1, (now - start) / ms);
            const t = ease(rawT);

            const x = bezAt(sx, c1x, c2x, tx, t);
            const y = bezAt(sy, c1y, c2y, ty, t);

            cursor.el.style.left = `${x}px`;
            cursor.el.style.top = `${y}px`;

            // Update store with viewport coords
            const r = container!.getBoundingClientRect();
            moveCursorTo(cursor.store, r.left + x, r.top + y);

            cx = x;
            cy = y;

            if (rawT < 1) {
              requestAnimationFrame(tick);
            } else {
              resolve();
            }
          }
          requestAnimationFrame(tick);
        });

        // Pause — cursor stays active in store (dots stay revealed around it)
        await sleep(500 + Math.random() * 1500);
      }
    }

    for (let i = 0; i < cursors.length; i++) {
      animateCursor(cursors[i], i).catch(() => {});
    }

    return () => {
      cancelled = true;
      for (const cursor of cursors) {
        deactivateCursor(cursor.store);
        cursor.el.remove();
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}
